// Shared status metadata — single source of truth for foreclosure status
// display across the frontend.
//
// Before this file landed (PR-A of the 2026-04-30 redesign), the same
// status enum was decoded in 3 places with subtly different keys:
//   - components/map.tsx — color-only map, included `res_plus` and `in_rem`
//   - components/PropertyDetail.tsx — `statusLabels` with desc strings
//   - components/PropertyCard.tsx — color map for the dot + lifecycle bar
//
// A new backend status (e.g. `postponed`, `auction_passed` from the
// status-transitioner work, #171/#176) had to be added in 3 places or
// the UI silently fell back to default-blue. The risk is real: the
// transitioner already ships those values, and they were missing from
// the frontend maps.
//
// Pattern mirrors `propertyLabels.ts` (#184). One registry, one fallback,
// tests pin the no-orphan-key contract.
//
// ─── Semantic color buckets (2026-08-21 redesign) ───────────────────────
//
// Color encodes the PRODUCT-LEVEL bucket, not the procedural status. The
// product's pinned definitions (docs/RELAUNCH.md, resolved 2026-08-05)
// already collapse the status enum into three surfaces, and the public
// dashboard (`lib/publicCountyStats.ts`) has always rendered exactly these
// three — the 12-color in-app map was the legacy inconsistency:
//
//   auction  → red   — the live scheduled-sale / upset-bid lifecycle
//                      (auction_scheduled, pending_confirmation, postponed)
//   lead     → blue  — a distress filing exists, no live sale yet
//                      (notice_of_default, notice_filed, in_rem, res_plus)
//   resolved → gray  — no longer active
//                      (sold, redeemed, withdrawn, auction_passed, delisted,
//                      case_concluded)
//
// Fine distinctions (which KIND of auction/lead/resolved) are carried by
// labels, pills, and detail views — never by color alone. See
// docs/design/DESIGN_SYSTEM.md §3.
//
// Color values stay aligned with index.css `--status-*` tokens. (Hard-coded
// here because <Pie> and <SimpleMarkerSymbol> can't read CSS vars at
// runtime; keep these in sync with :root if you re-theme.)

export type LifecycleStage = 0 | 1 | 2 | 3;
// 0 = NOD, 1 = Notice Filed, 2 = Auction, 3 = Resolved

export type StatusBucket = "auction" | "lead" | "resolved" | "distress";

export interface BucketMeta {
  key: StatusBucket;
  /** Display label for the map legend and grouped views. */
  label: string;
  /** The bucket's color — the ONLY colors map pins may use. */
  color: string;
  /** One-line explanation for legend tooltips. */
  desc: string;
}

/** The three semantic colors. Everything status-colored derives from these. */
export const BUCKET_META: Record<StatusBucket, BucketMeta> = {
  auction: {
    key: "auction",
    label: "Auction",
    color: "#ef4444",
    desc: "Live scheduled-sale / upset-bid lifecycle — a sale is scheduled, postponed, or awaiting confirmation. Where auction buyers act.",
  },
  lead: {
    key: "lead",
    label: "Lead",
    color: "#3b82f6",
    desc: "Pre-auction distress signal — a filing or notice exists but no live sale yet. Where off-market outreach starts.",
  },
  resolved: {
    key: "resolved",
    label: "Resolved",
    color: "#94a3b8",
    desc: "No longer active — sold, redeemed, withdrawn, passed without records, or delisted from the source.",
  },
  distress: {
    key: "distress",
    label: "Code Violation",
    color: "#f59e0b",
    desc: "Physical distress with no legal proceeding — a condemnation order, an open structural case, or years of repeat neglect. Nothing has been filed against the title.",
  },
};

export interface StatusMeta {
  /** Backend enum key, e.g. "auction_scheduled" */
  key: string;
  /** Display label for full UIs, e.g. "Auction Scheduled" */
  label: string;
  /** Compact label for card chips / map legend, e.g. "Auction" */
  short: string;
  /**
   * Hex color used for the status pill, dot, and map pin. ALWAYS the
   * status's bucket color (see BUCKET_META) — never a per-status hue.
   */
  color: string;
  /** Long-form description for the StatusHelpPopover (#183) */
  desc: string;
  /** Which of the 4 lifecycle bar segments this status occupies */
  lifecycleStage: LifecycleStage;
  /** True for terminal states (sold/redeemed/withdrawn/auction_passed/case_concluded/delisted). Drives "no further transitions" UX and zero actionability in the deal score. */
  isTerminal: boolean;
  /** Product-level semantic bucket (docs/RELAUNCH.md pinned definitions). */
  bucket: StatusBucket;
}

/**
 * Master registry. Add new statuses HERE — every consumer reads from
 * `statusMeta(key)` so a single edit propagates everywhere. Assign the
 * bucket per the pinned definitions in docs/RELAUNCH.md; `color` must be
 * that bucket's color.
 *
 * Coverage check: this should mirror `terminalStatuses` and the active
 * statuses in `backend/internal/status/transitioner.go`. Tests in
 * statusMeta.test.ts pin the no-orphan-key contract and the
 * color-equals-bucket-color contract.
 */
export const STATUS_META: Record<string, StatusMeta> = {
  notice_of_default: {
    key: "notice_of_default",
    label: "Notice of Default",
    short: "Pre-foreclosure",
    color: BUCKET_META.lead.color,
    desc: "Pre-foreclosure: a notice of default has been filed at the Register of Deeds. Typically 90+ days before auction.",
    lifecycleStage: 0,
    isTerminal: false,
    bucket: "lead",
  },
  notice_filed: {
    key: "notice_filed",
    label: "Notice Filed",
    short: "Notice Filed",
    color: BUCKET_META.lead.color,
    desc: "Foreclosure petition filed with the court. No sale date has been set yet.",
    lifecycleStage: 1,
    isTerminal: false,
    bucket: "lead",
  },
  auction_scheduled: {
    key: "auction_scheduled",
    label: "Auction Scheduled",
    short: "Auction",
    color: BUCKET_META.auction.color,
    desc: "Law firm has published a Notice of Sale with a specific date and time. Active auction.",
    lifecycleStage: 2,
    isTerminal: false,
    bucket: "auction",
  },
  sold: {
    key: "sold",
    label: "Sold",
    short: "Sold",
    color: BUCKET_META.resolved.color,
    desc: "Property sold at auction to the highest bidder.",
    lifecycleStage: 3,
    isTerminal: true,
    bucket: "resolved",
  },
  redeemed: {
    key: "redeemed",
    label: "Redeemed",
    short: "Redeemed",
    color: BUCKET_META.resolved.color,
    desc: "Owner paid the delinquent taxes before the sale. Foreclosure cancelled.",
    lifecycleStage: 3,
    isTerminal: true,
    bucket: "resolved",
  },
  withdrawn: {
    key: "withdrawn",
    label: "Withdrawn",
    short: "Withdrawn",
    color: BUCKET_META.resolved.color,
    desc: "Sale was withdrawn — case abandoned or settled outside the auction.",
    lifecycleStage: 3,
    isTerminal: true,
    bucket: "resolved",
  },
  code_violation: {
    key: "code_violation",
    label: "Code Violation",
    short: "Code Violation",
    color: BUCKET_META.distress.color,
    desc: "Municipal code-enforcement distress — condemnation, an open structural case, or sustained repeat neglect. No foreclosure has been filed and none may ever be.",
    // Stage 0, not 3: this is upstream of the foreclosure ladder, not the end
    // of it. It is also NOT terminal — a violation property that later enters
    // foreclosure must be free to transition (ADR 0037 §3).
    lifecycleStage: 0,
    isTerminal: false,
    bucket: "distress",
  },
  postponed: {
    key: "postponed",
    label: "Postponed",
    short: "Postponed",
    color: BUCKET_META.auction.color,
    desc: "Sale was postponed to a future date but not cancelled. May reschedule and re-enter active status.",
    // A LIVE auction whose date moved (part of the pinned "Auctions"
    // definition in docs/RELAUNCH.md; #1465). Stage 2, not 3, and not
    // terminal: /leads, the upset digest and the backend status buckets all
    // treat it as live, and scoring it as dead sank it below every live lead
    // (founder decision 2026-10-12).
    lifecycleStage: 2,
    isTerminal: false,
    bucket: "auction",
  },
  auction_passed: {
    key: "auction_passed",
    label: "Auction Passed",
    short: "Auction Passed",
    color: BUCKET_META.resolved.color,
    desc: "The scheduled auction date passed and we have no sale records for it — outcome genuinely unknown, pending a Tyler Portal lookup. (A property we can see sale records for shows as 'Pending Confirmation' or 'Sold' instead.)",
    lifecycleStage: 3,
    isTerminal: true,
    bucket: "resolved",
  },
  pending_confirmation: {
    key: "pending_confirmation",
    label: "Pending Confirmation",
    short: "Pending Confirm",
    color: BUCKET_META.auction.color,
    desc: "The sale was held and the 10-day upset-bid period has closed, but the court hasn't entered an order confirming the sale (and the deed isn't recorded) yet. The outcome is effectively decided — awaiting paperwork, which can take weeks. A confirmation moves it to 'Sold'; a new upset bid re-opens it.",
    // Stage 3 because the sale has been held (the card shows the final bid and
    // spread), but NOT terminal: until the court confirms, an upset bid can
    // still reopen it, and /leads, the upset digest and the backend buckets all
    // treat it as live. It gets a real deal score (founder decision
    // 2026-10-12). Part of the pinned "Auctions" definition, so bucket
    // "auction".
    lifecycleStage: 3,
    isTerminal: false,
    bucket: "auction",
  },
  case_concluded: {
    key: "case_concluded",
    label: "Case Concluded",
    short: "Case Concluded",
    color: BUCKET_META.resolved.color,
    desc: "The court has closed this case, and the case record doesn't show how it ended — no sale, dismissal or withdrawal is on file. It is no longer listed as an upcoming auction or an open lead. If a sale or a new sale date appears later, it moves back automatically.",
    lifecycleStage: 3,
    // Display-terminal (resolved column, no actionable deal score) like
    // auction_passed — but the backend keeps it re-decidable: later evidence
    // re-opens it (#2298).
    isTerminal: true,
    bucket: "resolved",
  },
  delisted: {
    key: "delisted",
    label: "No Longer Listed",
    short: "Delisted",
    color: BUCKET_META.resolved.color,
    desc: "No longer appears on the source's foreclosure list. The case may have been redeemed, settled, or removed — we can't confirm which, so it's marked delisted rather than redeemed. If it returns to the source it reactivates automatically.",
    lifecycleStage: 3,
    // Terminal for DISPLAY (resolved column, no actionable deal score), like
    // postponed — though the backend can reactivate it if the parcel reappears.
    isTerminal: true,
    bucket: "resolved",
  },
  in_rem: {
    key: "in_rem",
    label: "In Rem",
    short: "In Rem",
    color: BUCKET_META.lead.color,
    desc: "In rem tax-foreclosure proceeding (NC §105-375) — county levies a tax claim against the property itself rather than its owner.",
    lifecycleStage: 1,
    isTerminal: false,
    bucket: "lead",
  },
  res_plus: {
    key: "res_plus",
    label: "Residential Plus",
    short: "Residential+",
    color: BUCKET_META.lead.color,
    desc: "Residential property with an additional procedural step beyond a standard notice-filed proceeding.",
    lifecycleStage: 1,
    isTerminal: false,
    bucket: "lead",
  },
};

/**
 * Default fallback for unknown status keys. Preserves graceful render
 * while flagging the problem visually: muted resolved-gray, NEVER a live
 * bucket color — an unmapped backend value must not masquerade as an
 * active opportunity (it used to render as notice_of_default blue).
 *
 * If a real backend status hits this fallback, ADD it to STATUS_META —
 * statusMeta.test.ts will fail otherwise on the no-orphan contract.
 */
const UNKNOWN_STATUS: StatusMeta = {
  key: "unknown",
  label: "Unknown",
  short: "Unknown",
  color: BUCKET_META.resolved.color,
  desc: "Status not recognized — likely a new backend value not yet mapped in the frontend.",
  lifecycleStage: 1,
  isTerminal: false,
  bucket: "resolved",
};

/**
 * Look up the metadata for a status key. Always returns a StatusMeta
 * (never null) so callers can write code without null checks. Unknown
 * keys get the UNKNOWN_STATUS fallback (gray dot, "Unknown" label).
 *
 * @example
 * statusMeta("auction_scheduled").color  // "#ef4444"
 * statusMeta("auction_scheduled").short  // "Auction"
 * statusMeta(null).label                 // "Unknown"
 */
export function statusMeta(key: string | null | undefined): StatusMeta {
  if (!key) return UNKNOWN_STATUS;
  return STATUS_META[key] ?? UNKNOWN_STATUS;
}

/**
 * Convenience: just the color for a status. Use this when you only
 * need the hex value (e.g. ArcGIS SimpleMarkerSymbol). Otherwise prefer
 * the full statusMeta() so other fields are at hand.
 */
export function statusColor(key: string | null | undefined): string {
  return statusMeta(key).color;
}

/**
 * The semantic bucket for a status key (unknown keys → "resolved", the
 * muted fallback). Use this — never a hand-maintained status list — when
 * grouping by Auction / Lead / Resolved. Mirrors the pinned definitions
 * in docs/RELAUNCH.md and `publicActiveStatuses` in
 * backend/internal/handlers/public_handler.go (see issue #1626 on
 * consolidating the remaining hand-copied lists).
 */
export function statusBucket(key: string | null | undefined): StatusBucket {
  return statusMeta(key).bucket;
}
